import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/layout';
import api, { getAssetUrl } from '../utils/api';
import { toast } from 'react-toastify';
import { 
  Mail, MessageSquare, CheckCircle, Clock, Send, User, FileText, 
  MapPin, Undo2, Users, Home, Settings, Star, Shield, ChevronDown
} from 'lucide-react';
import ShareButton from '../components/ShareButton';
import { StyledUsername, FramedAvatar } from '../components/cosmetics';

const REGINA_WARDS = [
  { id: 'Ward 1', name: 'Ward 1 - Arnheim Place / North Central' },
  { id: 'Ward 2', name: 'Ward 2 - Rosemont / Mount Royal' },
  { id: 'Ward 3', name: 'Ward 3 - Cathedral / Core / Downtown' },
  { id: 'Ward 4', name: 'Ward 4 - Dewdney East / Eastview' },
  { id: 'Ward 5', name: 'Ward 5 - Albert Park / Lakeview' },
  { id: 'Ward 6', name: 'Ward 6 - Normanview / Sherwood Estates' },
  { id: 'Ward 7', name: 'Ward 7 - Harbour Landing / Whitmore Park' },
  { id: 'Ward 8', name: 'Ward 8 - Hillsdale / Glencairn' },
  { id: 'Ward 9', name: 'Ward 9 - Wascana View / The Creeks' },
  { id: 'Ward 10', name: 'Ward 10 - Arcola East / Greens on Gardiner' },
];

const Dashboard = () => {
  const [messages, setMessages] = useState([]);
  const [myPosts, setMyPosts] = useState([]);
  const [wardPosts, setWardPosts] = useState([]);
  const [wardStats, setWardStats] = useState(null);
  const [userBadges, setUserBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('community');
  const [showMainFeed, setShowMainFeed] = useState(false);
  const [messageFilter, setMessageFilter] = useState('all');
  const [wardComments, setWardComments] = useState({});
  const [wardExpandedComments, setWardExpandedComments] = useState({});
  const [wardNewComment, setWardNewComment] = useState({});
  const [realtorAd, setRealtorAd] = useState(null);
  // Ward Pulse V2
  const [wardPulseV2, setWardPulseV2] = useState(null);
  const [pulseV2Loading, setPulseV2Loading] = useState(false);
  // Alerts + ad
  const [alerts, setAlerts] = useState([]);
  const [displayAd, setDisplayAd] = useState(null);
  const [showWardSelector, setShowWardSelector] = useState(false);
  const [wardChangeLoading, setWardChangeLoading] = useState(false);
  const { user, loading: authLoading, isAdmin, updateUser } = useAuth();
  const navigate = useNavigate();

  const isAdminUser = isAdmin();
  const userBadgeCodes = (user?.badges || user?.user_badges || [])
    .map((badge) => badge.code || badge.badge_code || badge)
    .filter(Boolean);
  const hasModeratorBadge = userBadgeCodes.includes('community-leader') || userBadgeCodes.includes('community_leader');
  const hasModerationAccess = isAdminUser || user?.role === 'community_leader' || hasModeratorBadge;

  useEffect(() => {
    if (authLoading) return; // Wait for auth to finish loading before deciding
    if (!user) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [user, authLoading, navigate, showMainFeed]);

  useEffect(() => {
    if (user?.ward) {
      const wardNum = user.ward.replace('Ward ', '');
      api.get(`/ward-realtor-ads/${wardNum}`)
        .then(r => { if (r.data) setRealtorAd(r.data); })
        .catch(() => {});
      // Ward Pulse V2
      api.get(`/ward-pulse-v2/${encodeURIComponent(user.ward)}`)
        .then(r => { setWardPulseV2(r.data); })
        .catch(() => {});
      // Alerts + general ad
      api.get('/community-alerts?limit=5').then(r => setAlerts(r.data?.alerts || [])).catch(() => {});
      api.get('/ads').then(r => {
        const active = (r.data || []).filter(a => a.is_active !== false);
        if (active.length) setDisplayAd(active[Math.floor(Math.random() * active.length)]);
      }).catch(() => {});
    }
  }, [user?.ward]);


  const fetchData = async () => {
    try {
      const [messagesRes, myPostsRes, badgesRes] = await Promise.all([
        api.get('/messages/mine'),
        api.get('/posts/mine'),  // Get all user's posts (including ward_only)
        api.get('/badges/my-badges'), // Fetch real earned badges
      ]);
      setMessages(messagesRes.data);
      setMyPosts(myPostsRes.data.posts || []);
      setUserBadges(badgesRes.data.badges || []);

      // Fetch ward community posts and stats if user has a ward
      if (user.ward) {
        try {
          const [wardPostsRes, wardStatsRes] = await Promise.all([
            api.get(`/posts/my-ward?include_main_feed=${showMainFeed}`),
            api.get('/my-ward-stats'),
          ]);
          setWardPosts(wardPostsRes.data.posts || []);
          setWardStats(wardStatsRes.data);
        } catch (e) {
          console.log('Ward data not available');
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchWardComments = async (postId) => {
    try {
      const res = await api.get(`/posts/${postId}/comments`);
      setWardComments((prev) => ({ ...prev, [postId]: res.data }));
    } catch (error) {
      toast.error('Failed to load comments');
    }
  };

  const toggleWardComments = (postId) => {
    setWardExpandedComments((prev) => {
      const nextState = !prev[postId];
      if (nextState && !wardComments[postId]) {
        fetchWardComments(postId);
      }
      return { ...prev, [postId]: nextState };
    });
  };

  const handleWardCommentSubmit = async (postId) => {
    if (!user) {
      toast.error('Please log in to comment');
      return;
    }
    const content = (wardNewComment[postId] || '').trim();
    if (!content) {
      toast.error('Comment cannot be empty');
      return;
    }
    try {
      const res = await api.post(`/posts/${postId}/comments`, { content });
      setWardComments((prev) => ({
        ...prev,
        [postId]: [...(prev[postId] || []), res.data]
      }));
      setWardNewComment((prev) => ({ ...prev, [postId]: '' }));
      setWardPosts((prev) =>
        prev.map((post) =>
          post.id === postId ? { ...post, comment_count: (post.comment_count || 0) + 1 } : post
        )
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add comment');
    }
  };

  const handleWardPostModeration = async (postId, nextStatus) => {
    try {
      if (isAdminUser) {
        const action = nextStatus === 'moderated' ? 'reject' : 'approve';
        await api.put(`/admin/posts/${postId}/moderate?action=${action}`);
      } else {
        await api.put(`/community-leader/posts/${postId}/moderate?status=${nextStatus}`);
      }
      setWardPosts((prev) =>
        prev.map((post) => (post.id === postId ? { ...post, status: nextStatus } : post))
      );
      toast.success(nextStatus === 'moderated' ? 'Post hidden' : 'Post restored');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update post');
    }
  };

  const handleWardChange = async (ward) => {
    setWardChangeLoading(true);
    try {
      await api.put('/auth/change-ward', { ward });
      if (updateUser) {
        updateUser({ ...user, ward });
      }
      toast.success(`Ward updated to ${ward}!`);
      setShowWardSelector(false);
      setSelectedWard(ward);
      fetchData();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to update ward';
      toast.error(msg);
    } finally {
      setWardChangeLoading(false);
    }
  };

  const handleWardSelectionSave = () => {
    // Ward is set during registration. This is unused but kept for safety.
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const handleResolve = async (postId) => {
    try {
      const res = await api.post(`/posts/${postId}/resolve`);
      toast.success(`Issue marked as resolved! Took ${res.data.resolution_days} days.`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resolve post');
    }
  };

  const handleUnresolve = async (postId) => {
    try {
      await api.post(`/posts/${postId}/unresolve`);
      toast.success('Resolved status removed');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update post');
    }
  };

  const handleToggleFeatured = async (postId, currentFeaturedStatus) => {
    try {
      const newStatus = !currentFeaturedStatus;
      await api.put(`/admin/posts/${postId}/feature`, null, {
        params: { is_featured: newStatus }
      });
      toast.success(newStatus ? 'Post featured!' : 'Post unfeatured');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle featured status');
    }
  };

  if (authLoading) return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#C5A52A]"></div>
    </div>
  );

  if (!user) return null;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-8 overflow-x-hidden">

        {/* Ward Identity Header */}
        <div className="bg-[#111] border border-[#1a1a1a] rounded p-4 mb-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            {user.profile_image_url ? (
              <img
                src={getAssetUrl(user.profile_image_url)}
                alt={user.name}
                className="w-10 h-10 rounded-full object-cover flex-shrink-0"
              />
            ) : (
              <div className="w-10 h-10 bg-[#1a1a1a] rounded-full flex items-center justify-center flex-shrink-0">
                <User className="w-5 h-5 text-gray-500" />
              </div>
            )}
            <div>
              <p className="font-display text-white text-base leading-tight">
                {user.username?.toUpperCase() || user.name?.toUpperCase()}
              </p>
              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                {/* Ward Badge - with change option */}
                {user.ward ? (
                  <div className="relative">
                    <button
                      onClick={() => setShowWardSelector(!showWardSelector)}
                      className="flex items-center gap-1 bg-blue-900/30 border border-blue-800/50 rounded px-2 py-0.5 hover:border-blue-600/70 transition-colors"
                      title="Click to change ward (once every 90 days)"
                      data-testid="ward-change-btn"
                    >
                      <MapPin className="w-3 h-3 text-blue-400" />
                      <span className="font-mono text-[10px] text-blue-400">{user.ward}</span>
                      <ChevronDown className="w-3 h-3 text-blue-400/60" />
                    </button>
                    {showWardSelector && (
                      <>
                        <div className="fixed inset-0 z-10" onClick={() => setShowWardSelector(false)} />
                        <div className="absolute top-full left-0 mt-1 bg-[#0a0a0a] border border-[#2a2a2a] rounded shadow-xl z-20 min-w-[260px]">
                          <div className="p-2 border-b border-[#1a1a1a]">
                            <p className="font-mono text-[10px] text-gray-500 uppercase">Change Ward (once per 90 days)</p>
                          </div>
                          <div className="max-h-48 overflow-y-auto">
                            {REGINA_WARDS.map((w) => (
                              <button
                                key={w.id}
                                onClick={() => handleWardChange(w.id)}
                                disabled={wardChangeLoading || w.id === user.ward}
                                className={`w-full text-left px-3 py-2 text-xs font-mono transition-colors ${
                                  w.id === user.ward
                                    ? 'text-[#C5A52A] bg-[#C5A52A]/10 cursor-default'
                                    : 'text-gray-300 hover:bg-[#1a1a1a] hover:text-white'
                                }`}
                                data-testid={`ward-option-${w.id.replace(' ', '-').toLowerCase()}`}
                              >
                                {w.id === user.ward ? `✓ ${w.name}` : w.name}
                              </button>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="relative">
                    <button
                      onClick={() => setShowWardSelector(!showWardSelector)}
                      className="flex items-center gap-1 bg-[#1a1a1a] border border-[#2a2a2a] rounded px-2 py-0.5 hover:border-[#C5A52A]/50 transition-colors"
                      data-testid="ward-select-btn"
                    >
                      <MapPin className="w-3 h-3 text-gray-500" />
                      <span className="font-mono text-[10px] text-gray-400">SET WARD</span>
                      <ChevronDown className="w-3 h-3 text-gray-400/60" />
                    </button>
                    {showWardSelector && (
                      <>
                        <div className="fixed inset-0 z-10" onClick={() => setShowWardSelector(false)} />
                        <div className="absolute top-full left-0 mt-1 bg-[#0a0a0a] border border-[#2a2a2a] rounded shadow-xl z-20 min-w-[260px]">
                          <div className="p-2 border-b border-[#1a1a1a]">
                            <p className="font-mono text-[10px] text-gray-500 uppercase">Select Your Ward</p>
                          </div>
                          <div className="max-h-48 overflow-y-auto">
                            {REGINA_WARDS.map((w) => (
                              <button
                                key={w.id}
                                onClick={() => handleWardChange(w.id)}
                                disabled={wardChangeLoading}
                                className="w-full text-left px-3 py-2 text-xs font-mono text-gray-300 hover:bg-[#1a1a1a] hover:text-white transition-colors"
                                data-testid={`ward-option-${w.id.replace(' ', '-').toLowerCase()}`}
                              >
                                {w.name}
                              </button>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}
                {user.is_verified && (
                  <span className="flex items-center gap-1">
                    <CheckCircle className="w-3 h-3 text-green-500" />
                    <span className="font-mono text-[10px] text-green-500">VERIFIED</span>
                  </span>
                )}
                {/* Show top badge */}
                {userBadges.length > 0 && (
                  <span className="font-mono text-[10px] text-[#C5A52A]">
                    🏅 {userBadges[0].badge_name}
                    {userBadges.length > 1 && ` +${userBadges.length - 1}`}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to={`/profile/${user.id}`}
              className="font-mono text-[10px] text-gray-400 border border-[#2a2a2a] px-3 py-1.5 hover:text-white hover:border-[#C5A52A]/50 transition-colors"
              title="View your public profile"
            >
              PUBLIC PROFILE
            </Link>
            <Link
              to="/account"
              className="flex items-center gap-1.5 font-mono text-[10px] text-gray-400 border border-[#2a2a2a] px-3 py-1.5 hover:text-white hover:border-[#C5A52A]/50 transition-colors"
              data-testid="account-settings-link"
              title="Account settings, cosmetics, photo"
            >
              <Settings className="w-3 h-3" />
              SETTINGS
            </Link>
          </div>
        </div>

        {/* Mini Realtor Ad */}
        {realtorAd && (
          <div className="bg-[#0d0d0d] border border-[#C5A52A]/20 rounded overflow-hidden mb-4">
            <div className="flex items-stretch gap-0">
              {realtorAd.image_url && (
                <div className="w-28 flex-shrink-0 relative">
                  <img
                    src={realtorAd.image_url}
                    alt={realtorAd.business_name}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                </div>
              )}
              <div className="flex-1 p-4 flex flex-col justify-between gap-2">
                <div>
                  <p className="font-mono text-[10px] text-[#C5A52A]/70 uppercase tracking-widest mb-1">Local Expert · {user.ward}</p>
                  <p className="font-display text-base text-white leading-tight">{realtorAd.business_name}</p>
                  {realtorAd.expert_name && (
                    <p className="font-mono text-[10px] text-[#C5A52A] mt-0.5">FEATURED: {realtorAd.expert_name}</p>
                  )}
                  {realtorAd.description && (
                    <p className="text-gray-400 text-xs mt-2 leading-relaxed line-clamp-2">{realtorAd.description}</p>
                  )}
                </div>
                {realtorAd.cta_visible !== false && realtorAd.cta_url && (
                  <a
                    href={realtorAd.cta_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="self-start bg-[#C5A52A] hover:bg-[#D4B93A] text-black font-display text-[10px] px-4 py-1.5 tracking-wider transition-colors whitespace-nowrap"
                  >
                    {realtorAd.cta_label || 'LEARN MORE'}
                  </a>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Community Alerts Widget */}
        {alerts.length > 0 && (
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-1.5 h-1.5 rounded-full bg-[#C5A52A] animate-pulse" />
              <span className="font-mono text-[10px] text-[#C5A52A] uppercase tracking-widest">Regina Updates</span>
            </div>
            <div className="space-y-2">
              {alerts.map(alert => {
                const catStyles = {
                  alert: 'text-red-400 border-red-900/50 bg-red-950/20',
                  news: 'text-[#C5A52A] border-[#C5A52A]/30 bg-[#C5A52A]/5',
                  community: 'text-green-400 border-green-900/50 bg-green-950/20',
                };
                const style = catStyles[alert.category] || catStyles.news;
                return (
                  <div key={alert.id} className={`border rounded px-3 py-2.5 ${style}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <span className={`font-mono text-[9px] uppercase tracking-widest mr-2 opacity-70`}>
                          {alert.category === 'alert' ? '⚠ ALERT' : alert.category === 'community' ? '◆ COMMUNITY' : '◆ NEWS'}
                        </span>
                        <span className="text-white text-xs font-mono">{alert.title}</span>
                        {alert.body && <p className="text-gray-400 text-xs mt-1 leading-relaxed">{alert.body}</p>}
                      </div>
                      {alert.link_url && (
                        <a
                          href={alert.link_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-mono text-[9px] uppercase tracking-widest whitespace-nowrap opacity-80 hover:opacity-100 transition-opacity flex-shrink-0"
                        >
                          {alert.link_label || 'Read More'} →
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Standard Display Ad */}
        {displayAd && (
          <div className="mb-4 border border-[#1a1a1a] rounded overflow-hidden">
            {displayAd.image_url && (
              <a href={displayAd.link || '#'} target="_blank" rel="noopener noreferrer">
                <img src={displayAd.image_url} alt={displayAd.title} className="w-full object-cover max-h-24" />
              </a>
            )}
            {!displayAd.image_url && (
              <div className="bg-[#0d0d0d] px-4 py-3 flex items-center justify-between">
                <div>
                  <p className="font-mono text-[9px] text-gray-600 uppercase tracking-widest mb-0.5">Sponsored</p>
                  <p className="text-white text-xs">{displayAd.title}</p>
                  {displayAd.content && <p className="text-gray-500 text-[10px] mt-0.5">{displayAd.content}</p>}
                </div>
                {displayAd.link && (
                  <a href={displayAd.link} target="_blank" rel="noopener noreferrer"
                    className="font-mono text-[9px] text-[#C5A52A] hover:text-white transition-colors whitespace-nowrap ml-3">
                    Learn More →
                  </a>
                )}
              </div>
            )}
          </div>
        )}

        {/* Ward Stats Banner */}
        {wardStats && wardStats.councillor && (
          <div className="bg-gradient-to-r from-[#C5A52A]/20 to-[#C5A52A]/5 border border-[#C5A52A]/30 rounded p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img 
                  src={wardStats.councillor.image_url} 
                  alt={wardStats.councillor.name}
                  className="w-10 h-10 rounded-full object-cover"
                  onError={(e) => {
                    e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(wardStats.councillor.name)}&background=1a1a1a&color=fff`;
                  }}
                />
                <div>
                  <p className="font-display text-sm text-white">{wardStats.councillor.name}</p>
                  <p className="font-mono text-[10px] text-[#C5A52A]">{wardStats.ward} COUNCILLOR</p>
                </div>
              </div>
              <div className="text-right">
                <p className="font-mono text-xs text-gray-400">THIS MONTH</p>
                <p className="font-display text-sm text-white">
                  <span className="text-[#C5A52A]">{wardStats.this_month_sent}</span> messages sent • 
                  <span className="text-green-500"> {wardStats.this_month_replied}</span> replied
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6 overflow-x-auto">
          <button
            onClick={() => setActiveTab('community')}
            className={`flex items-center gap-2 px-4 py-2 font-display text-sm tracking-wider transition-colors whitespace-nowrap ${
              activeTab === 'community'
                ? 'bg-[#C5A52A] text-black'
                : 'bg-[#1a1a1a] text-gray-400 hover:text-white'
            }`}
          >
            <Users className="w-4 h-4" />
            WARD COMMUNITY
          </button>
          <button
            onClick={() => setActiveTab('posts')}
            className={`flex items-center gap-2 px-4 py-2 font-display text-sm tracking-wider transition-colors whitespace-nowrap ${
              activeTab === 'posts'
                ? 'bg-[#C5A52A] text-black'
                : 'bg-[#1a1a1a] text-gray-400 hover:text-white'
            }`}
          >
            <FileText className="w-4 h-4" />
            MY POSTS ({myPosts.length})
          </button>
          <button
            onClick={() => setActiveTab('messages')}
            className={`flex items-center gap-2 px-4 py-2 font-display text-sm tracking-wider transition-colors whitespace-nowrap ${
              activeTab === 'messages'
                ? 'bg-[#C5A52A] text-black'
                : 'bg-[#1a1a1a] text-gray-400 hover:text-white'
            }`}
          >
            <MessageSquare className="w-4 h-4" />
            MESSAGES ({messages.length})
          </button>

        </div>

        {/* Ward Pulse V2 */}
        {user?.ward && (
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              <span className="font-mono text-xs text-red-400 tracking-[0.2em] uppercase">Ward Pulse</span>
              <span className="font-mono text-[10px] text-gray-600 ml-auto hidden sm:block">
                {wardPulseV2?.month_year || ''}
              </span>
            </div>

            {/* Question slots */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
              {[0, 1].map(slot => {
                const q = wardPulseV2?.questions?.[slot];
                const hasVoted = wardPulseV2?.user_voted_question_id;
                const votedThis = hasVoted === q?.id;
                const leadingQ = wardPulseV2?.questions?.[0];
                const isLeading = q && leadingQ && q.id === leadingQ.id && (wardPulseV2?.questions?.length > 1);

                return (
                  <div key={slot} className={`bg-[#0d0d0d] border rounded p-4 flex flex-col justify-between min-h-[140px] transition-all
                    ${q ? (votedThis ? 'border-red-600/60' : 'border-red-900/30') : 'border-[#1a1a1a] opacity-40'}`}>
                    {q ? (
                      <>
                        <div className="mb-3">
                          <p className="font-mono text-[10px] text-gray-600 uppercase tracking-widest mb-1">
                            @{q.username}
                          </p>
                          <p className="text-white text-sm leading-snug">{q.question_text}</p>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="font-mono text-[10px] text-gray-600">{q.vote_count} vote{q.vote_count !== 1 ? 's' : ''}{q.status === 'sent' ? ' · SENT' : ''}</span>
                          {q.status !== 'sent' && (
                            <button
                              onClick={async () => {
                                if (hasVoted || pulseV2Loading) return;
                                setPulseV2Loading(true);
                                try {
                                  const ward = user.ward.replace('Ward ', 'ward-');
                                  await api.post(`/ward-pulse-v2/${encodeURIComponent(user.ward)}/vote`, { question_id: q.id });
                                  const r = await api.get(`/ward-pulse-v2/${encodeURIComponent(user.ward)}`);
                                  setWardPulseV2(r.data);
                                } catch (e) { console.error(e); }
                                finally { setPulseV2Loading(false); }
                              }}
                              disabled={!!hasVoted || pulseV2Loading}
                              className={`font-mono text-[10px] tracking-widest px-3 py-1.5 border transition-all
                                ${votedThis ? 'border-red-600 text-red-400 bg-red-950/20' :
                                  hasVoted ? 'border-[#1a1a1a] text-gray-700 cursor-not-allowed' :
                                  'border-red-600 text-red-400 hover:bg-red-600 hover:text-white cursor-pointer'}`}
                            >
                              {votedThis ? '✓ VOTED' : 'VOTE'}
                            </button>
                          )}
                        </div>
                      </>
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full gap-2">
                        <p className="font-mono text-[10px] text-gray-700 uppercase tracking-widest text-center">
                          Question slot {slot + 1}
                        </p>
                        <p className="font-mono text-[9px] text-gray-800 text-center">Waiting for top earner</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Eligible user: submit question */}
            {wardPulseV2?.user_is_eligible && !wardPulseV2?.user_has_submitted && (
              <div className="bg-[#0a0a0a] border border-[#C5A52A]/40 rounded p-4 mb-4">
                <p className="font-mono text-[10px] text-[#C5A52A] uppercase tracking-widest mb-2">
                  You're in the top 2 this month — submit your question
                </p>
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  const text = e.target.question.value.trim();
                  if (!text) return;
                  setPulseV2Loading(true);
                  try {
                    await api.post(`/ward-pulse-v2/${encodeURIComponent(user.ward)}/submit`, { question_text: text });
                    const r = await api.get(`/ward-pulse-v2/${encodeURIComponent(user.ward)}`);
                    setWardPulseV2(r.data);
                    e.target.reset();
                  } catch (err) {
                    const msg = err?.response?.data?.detail || 'Failed to submit';
                    alert(msg);
                  } finally { setPulseV2Loading(false); }
                }}>
                  <textarea
                    name="question"
                    maxLength={300}
                    placeholder="What do you want to ask your councillor this month? (max 300 chars)"
                    className="w-full bg-[#111] border border-[#2a2a2a] text-white text-sm px-3 py-2 rounded mb-2 h-20 resize-none font-mono"
                    required
                  />
                  <button type="submit" disabled={pulseV2Loading}
                    className="font-mono text-[10px] tracking-widest px-4 py-2 bg-[#C5A52A] text-black hover:bg-yellow-400 disabled:opacity-50 rounded transition-colors">
                    SUBMIT QUESTION
                  </button>
                </form>
              </div>
            )}

            {wardPulseV2?.user_is_eligible && wardPulseV2?.user_has_submitted && (
              <p className="font-mono text-[10px] text-[#C5A52A] mb-4">✓ Your question is live this month</p>
            )}

            {/* Monthly scoreboard */}
            {wardPulseV2?.leaderboard?.length > 0 && (
              <div className="bg-[#0d0d0d] border border-[#1a1a1a] rounded p-4">
                <p className="font-mono text-[10px] text-gray-500 uppercase tracking-widest mb-3">
                  {user.ward} · Points Earned This Month
                </p>
                <div className="space-y-2">
                  {wardPulseV2.leaderboard.slice(0, 5).map((entry, i) => {
                    const isCurrentUser = entry.user_id === user?.id;
                    const isTop2 = i < 2;
                    return (
                      <div key={entry.user_id} className={`flex items-center gap-3 py-1.5 px-2 rounded transition-colors
                        ${isCurrentUser ? 'bg-[#C5A52A]/10' : ''}`}>
                        <span className={`font-mono text-xs w-5 text-right flex-shrink-0
                          ${i === 0 ? 'text-[#C5A52A]' : i === 1 ? 'text-gray-400' : 'text-gray-700'}`}>
                          #{i + 1}
                        </span>
                        <span className={`font-mono text-xs flex-1 truncate
                          ${isTop2 ? 'text-white' : 'text-gray-500'}
                          ${isCurrentUser ? 'text-[#C5A52A]' : ''}`}>
                          {entry.username}{isCurrentUser ? ' (you)' : ''}
                          {isTop2 && <span className="ml-1 text-[9px] text-[#C5A52A]">✦ ELIGIBLE</span>}
                        </span>
                        <span className={`font-mono text-xs flex-shrink-0
                          ${isTop2 ? 'text-white font-bold' : 'text-gray-600'}`}>
                          +{entry.points_this_month}
                        </span>
                      </div>
                    );
                  })}
                </div>
                {!wardPulseV2?.user_is_eligible && wardPulseV2?.user_rank && (
                  <p className="font-mono text-[9px] text-gray-700 mt-3 text-center">
                    You're #{wardPulseV2.user_rank} · Top 2 earn question slots
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Feature Tiles - Ward Photo Contest, QCCA */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {/* Ward Photo Contest Tile */}
            <Link 
              to={`/ward/${user.ward.replace('Ward ', '')}`}
              className="bg-gradient-to-br from-[#C5A52A]/10 to-[#0d0d0d] border border-[#C5A52A]/30 hover:border-[#C5A52A] rounded p-5 transition-all hover:scale-[1.02] group"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="w-10 h-10 rounded bg-[#C5A52A]/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <span className="text-2xl">📸</span>
                </div>
                <div className="bg-[#C5A52A]/20 px-2 py-1 rounded">
                  <span className="font-mono text-[9px] text-[#C5A52A] uppercase tracking-wider">Active</span>
                </div>
              </div>
              <h3 className="text-white font-display text-base mb-2 group-hover:text-[#C5A52A] transition-colors">
                Ward Photo Contest
              </h3>
              <p className="text-gray-500 text-xs leading-relaxed mb-3">
                Submit photos from your ward. Winner gets featured on the ward page.
              </p>
              <div className="flex items-center gap-2 text-[#C5A52A] text-xs font-mono">
                <span>View Contest</span>
                <span className="group-hover:translate-x-1 transition-transform">→</span>
              </div>
            </Link>

            {/* Community Association Tile */}
            <a 
              href="mailto:info@cyqr.ca?subject=Community Association Inquiry"
              className="bg-gradient-to-br from-purple-500/10 to-[#0d0d0d] border border-purple-500/30 hover:border-purple-500 rounded p-5 transition-all hover:scale-[1.02] group"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="w-10 h-10 rounded bg-purple-500/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <span className="text-2xl">🏘️</span>
                </div>
                <div className="bg-purple-500/20 px-2 py-1 rounded">
                  <span className="font-mono text-[9px] text-purple-400 uppercase tracking-wider">Coming Soon</span>
                </div>
              </div>
              <h3 className="text-white font-display text-base mb-2 group-hover:text-purple-400 transition-colors">
                Community Association
              </h3>
              <p className="text-gray-500 text-xs leading-relaxed mb-3">
                Are you an employee or board member of your community association? Click here to contact for more information.
              </p>
              <div className="flex items-center gap-2 text-purple-400 text-xs font-mono">
                <span>Contact Us</span>
                <span className="group-hover:translate-x-1 transition-transform">→</span>
              </div>
            </a>
          </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#C5A52A] mx-auto"></div>
            <p className="text-gray-500 mt-4 font-mono text-sm">Loading...</p>
          </div>
        ) : activeTab === 'community' ? (
          /* Ward Community Section */
          <>
            {!user.ward ? (
              <div className="text-center py-12 bg-[#111] border border-[#1a1a1a] rounded">
                <MapPin className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400 font-mono mb-4">Set your ward to see community posts</p>
                <button
                  onClick={() => setShowWardSelector(true)}
                  className="inline-flex items-center gap-2 bg-[#C5A52A] hover:bg-[#D4B93A] text-black font-display text-sm py-2 px-6 tracking-wider transition-colors"
                >
                  <Settings className="w-4 h-4" />
                  SELECT YOUR WARD
                </button>
              </div>
            ) : (
              <>
                {/* Toggle for main feed posts */}
                <div className="flex items-center justify-between mb-4 p-3 bg-[#111] border border-[#1a1a1a] rounded">
                  <div className="flex items-center gap-2">
                    <Home className="w-4 h-4 text-gray-500" />
                    <span className="font-mono text-xs text-gray-400">SHOW ISSUES FROM MAIN FEED</span>
                  </div>
                  <button
                    onClick={() => {
                      setShowMainFeed((prev) => !prev);
                    }}
                    className={`relative w-12 h-6 rounded-full transition-colors ${
                      showMainFeed ? 'bg-[#C5A52A]' : 'bg-gray-700'
                    }`}
                    data-testid="main-feed-toggle"
                  >
                    <span
                      className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                        showMainFeed ? 'left-7' : 'left-1'
                      }`}
                    />
                  </button>
                </div>

                <div className="flex items-center justify-between mb-4">
                  <p className="font-mono text-xs text-gray-500">
                    Posts from {user.ward} residents • Lost pets, crime watch, local notices
                  </p>
                  <Link
                    to="/submit"
                    className="flex items-center gap-2 bg-[#1a1a1a] hover:bg-[#2a2a2a] text-white font-display text-xs py-2 px-4 tracking-wider transition-colors"
                  >
                    <Send className="w-3 h-3" />
                    POST TO WARD
                  </Link>
                </div>

                {wardPosts.length === 0 ? (
                  <div className="text-center py-12 bg-[#111] border border-[#1a1a1a] rounded">
                    <Users className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-500 font-mono mb-4">No community posts in {user.ward} yet</p>
                    <Link
                      to="/submit"
                      className="inline-flex items-center gap-2 bg-[#C5A52A] hover:bg-[#D4B93A] text-black font-display text-sm py-2 px-6 tracking-wider transition-colors"
                    >
                      <Send className="w-4 h-4" />
                      BE THE FIRST TO POST
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {wardPosts
                      .filter((post) => post.status !== 'moderated' || hasModerationAccess)
                      .map((post) => (
                      <div
                        key={post.id}
                        className="bg-[#111] border border-[#2a2a2a] rounded overflow-hidden shadow-[0_0_0_1px_#1f1f1f]"
                        data-testid={`ward-post-${post.id}`}
                      >
                        <div className="p-4 border-b border-[#1a1a1a] flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <FramedAvatar
                              user={{ profile_image_url: post.user_profile_image, profile_frame: post.user_profile_frame, name: post.user_name }}
                              size={8}
                            />
                            <Link 
                              to={`/profile/${post.user_id}`}
                              className="font-display text-xs hover:text-[#C5A52A] transition-colors"
                            >
                              <StyledUsername user={{ accent_color: post.user_accent_color, name_style: post.user_name_style }}>
                                {post.user_name?.toUpperCase()}
                              </StyledUsername>
                            </Link>
                            <span className="font-mono text-[10px] text-gray-600">
                              #{post.id.slice(0, 8).toUpperCase()} • {formatDate(post.created_at)}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            {post.post_type === 'ward_only' && (
                              <span className="bg-blue-900/30 border border-blue-800/50 rounded px-2 py-0.5">
                                <span className="font-mono text-[10px] text-blue-400">WARD ONLY</span>
                              </span>
                            )}
                            {post.resolved_at ? (
                              <span className="bg-green-900/30 border border-green-800/50 rounded px-2 py-0.5">
                                <span className="font-mono text-[10px] text-green-500">RESOLVED</span>
                              </span>
                            ) : post.category === 'issue' ? (
                              <span className="bg-yellow-900/30 border border-yellow-800/50 rounded px-2 py-0.5">
                                <span className="font-mono text-[10px] text-yellow-500">
                                  {Math.floor((Date.now() - new Date(post.created_at)) / (1000 * 60 * 60 * 24))} DAYS
                                </span>
                              </span>
                            ) : null}
                            {!post.resolved_at && post.verification_status === 'fixed' && (
                              <span className="bg-emerald-900/30 border border-emerald-800/50 rounded px-2 py-0.5" data-testid={`ward-post-verified-${post.id}`}>
                                <span className="font-mono text-[10px] text-emerald-400">VERIFIED</span>
                              </span>
                            )}
                            {post.verification_status === 'still_issue' && (
                              <span className="bg-orange-900/30 border border-orange-800/50 rounded px-2 py-0.5" data-testid={`ward-post-still-issue-${post.id}`}>
                                <span className="font-mono text-[10px] text-orange-400">STILL AN ISSUE</span>
                              </span>
                            )}
                            {post.status === 'moderated' && (
                              <span className="bg-red-900/30 border border-red-800/50 rounded px-2 py-0.5" data-testid={`ward-post-hidden-${post.id}`}>
                                <span className="font-mono text-[10px] text-red-400">HIDDEN</span>
                              </span>
                            )}
                            {hasModerationAccess && (
                              <button
                                onClick={() => handleWardPostModeration(post.id, post.status === 'moderated' ? 'active' : 'moderated')}
                                className={`px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider border rounded ${
                                  post.status === 'moderated'
                                    ? 'bg-green-900/30 border-green-800/50 text-green-400'
                                    : 'bg-red-900/30 border-red-800/50 text-red-400'
                                }`}
                                data-testid={`ward-post-moderate-${post.id}`}
                              >
                                {post.status === 'moderated' ? 'RESTORE' : 'HIDE'}
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="p-4">
                          <p className="text-gray-300 text-sm">{post.content}</p>
                          {post.image_url && (
                            <img src={post.image_url} alt="Post" className="mt-3 rounded max-h-48 object-cover" />
                          )}
                        </div>
                        <div className="px-4 pb-4 flex items-center justify-between">
                          <button
                            onClick={() => toggleWardComments(post.id)}
                            className="flex items-center gap-2 text-gray-400 hover:text-white text-xs font-mono"
                            data-testid={`ward-comments-toggle-${post.id}`}
                          >
                            <MessageSquare className="w-4 h-4" />
                            {post.comment_count || 0} COMMENTS
                          </button>
                          <ShareButton
                            url={post.ward ? `/ward/ward-${post.ward.replace('Ward ', '')}` : '/dashboard'}
                            text={`Community issue reported in ${post.ward || 'Regina'}: "${(post.content || '').slice(0, 100)}${(post.content || '').length > 100 ? '...' : ''}" — via CYQR.CA`}
                            size="xs"
                          />
                        </div>
                        {wardExpandedComments[post.id] && (
                          <div className="px-4 pb-4 border-t border-[#1a1a1a]">
                            <div className="space-y-3 mt-3">
                              {(wardComments[post.id] || []).map((comment) => (
                                <div key={comment.id} className="bg-[#0a0a0a] border border-[#1a1a1a] rounded p-3">
                                  <div className="flex items-center justify-between mb-2">
                                    <Link
                                      to={`/profile/${comment.user_id}`}
                                      className="text-xs font-mono text-[#C5A52A] hover:underline"
                                    >
                                      {comment.user_name?.toUpperCase()}
                                    </Link>
                                    <span className="text-[10px] font-mono text-gray-500">{formatDate(comment.created_at)}</span>
                                  </div>
                                  <p className="text-gray-300 text-sm">{comment.content}</p>
                                </div>
                              ))}
                              {(wardComments[post.id] || []).length === 0 && (
                                <p className="text-gray-500 text-xs font-mono">No comments yet</p>
                              )}
                            </div>
                            <div className="mt-4">
                              {user ? (
                                <>
                                  <textarea
                                    rows={3}
                                    value={wardNewComment[post.id] || ''}
                                    onChange={(event) =>
                                      setWardNewComment((prev) => ({ ...prev, [post.id]: event.target.value }))
                                    }
                                    className="w-full bg-black border border-[#2a2a2a] text-white text-sm px-3 py-2 rounded"
                                    placeholder="Add a comment..."
                                    data-testid={`ward-comment-input-${post.id}`}
                                  />
                                  <div className="flex justify-end mt-2">
                                    <button
                                      onClick={() => handleWardCommentSubmit(post.id)}
                                      className="bg-[#C5A52A] hover:bg-[#D4B93A] text-black font-display text-xs px-4 py-2 tracking-wider"
                                      data-testid={`ward-comment-submit-${post.id}`}
                                    >
                                      POST COMMENT
                                    </button>
                                  </div>
                                </>
                              ) : (
                                <p className="text-gray-500 text-xs font-mono">
                                  <Link to="/login" className="text-[#C5A52A] hover:underline">Log in</Link> to comment
                                </p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </>
        ) : activeTab === 'posts' ? (
          /* My Posts Section */
          <>
            <div className="flex items-center justify-between mb-4">
              <p className="font-mono text-xs text-gray-500">
                Track your submitted issues and mark them as resolved
              </p>
              <Link
                to="/submit"
                className="flex items-center gap-2 bg-[#C5A52A] hover:bg-[#D4B93A] text-black font-display text-xs py-2 px-4 tracking-wider transition-colors"
              >
                <Send className="w-3 h-3" />
                NEW POST
              </Link>
            </div>

            {myPosts.length === 0 ? (
              <div className="text-center py-12 bg-[#111] border border-[#1a1a1a] rounded">
                <FileText className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-500 font-mono mb-4">You haven't submitted any posts yet</p>
                <Link
                  to="/submit"
                  className="inline-flex items-center gap-2 bg-[#C5A52A] hover:bg-[#D4B93A] text-black font-display text-sm py-2 px-6 tracking-wider transition-colors"
                >
                  <Send className="w-4 h-4" />
                  SUBMIT YOUR FIRST ISSUE
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {myPosts.map((post) => (
                  <div
                    key={post.id}
                    className={`bg-[#111] border rounded overflow-hidden ${
                      post.post_type === 'ward_only' 
                        ? 'border-blue-800/50' 
                        : 'border-[#1a1a1a]'
                    }`}
                    data-testid={`my-post-${post.id}`}
                  >
                    <div className="p-4 border-b border-[#1a1a1a] flex items-center justify-between">
                      <div>
                        <p className="font-mono text-[10px] text-gray-600">
                          #{post.id.slice(0, 8).toUpperCase()} • {formatDate(post.created_at)}
                          {post.address && ` • ${post.address}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {/* Post type badge */}
                        {post.post_type === 'ward_only' && (
                          <span className="flex items-center gap-1 bg-blue-900/30 border border-blue-800/50 rounded px-2 py-1">
                            <Users className="w-3 h-3 text-blue-400" />
                            <span className="font-mono text-[10px] text-blue-400">WARD ONLY</span>
                          </span>
                        )}
                        {/* Category badge */}
                        {post.category && post.category !== 'issue' && (
                          <span className={`flex items-center gap-1 rounded px-2 py-1 ${
                            post.category === 'positive' 
                              ? 'bg-green-900/30 border border-green-800/50' 
                              : 'bg-blue-900/30 border border-blue-800/50'
                          }`}>
                            <span className={`font-mono text-[10px] ${
                              post.category === 'positive' ? 'text-green-400' : 'text-blue-400'
                            }`}>
                              {post.category === 'positive' ? '⭐ POSITIVE' : '💬 DISCUSSION'}
                            </span>
                          </span>
                        )}
                        {post.resolved_at ? (
                          <span className="flex items-center gap-1 bg-green-900/30 border border-green-800/50 rounded px-2 py-1">
                            <CheckCircle className="w-3 h-3 text-green-500" />
                            <span className="font-mono text-[10px] text-green-500">
                              RESOLVED IN {post.resolution_days} DAYS
                            </span>
                          </span>
                        ) : post.category === 'issue' || !post.category ? (
                          <span className="flex items-center gap-1 bg-yellow-900/30 border border-yellow-800/50 rounded px-2 py-1">
                            <Clock className="w-3 h-3 text-yellow-500" />
                            <span className="font-mono text-[10px] text-yellow-500">
                              OUTSTANDING {Math.floor((Date.now() - new Date(post.created_at)) / (1000 * 60 * 60 * 24))} DAYS
                            </span>
                          </span>
                        ) : null}
                      </div>
                    </div>

                    <div className="p-4">
                      <p className="text-gray-300 leading-relaxed text-sm mb-4">{post.content}</p>
                      
                      {post.image_url && (
                        <img
                          src={post.image_url}
                          alt="Post attachment"
                          className="w-full max-w-md rounded mb-4"
                        />
                      )}

                      <div className="pt-4 border-t border-[#1a1a1a] flex items-center justify-between">
                        <div className="flex items-center gap-4 text-gray-500 font-mono text-xs">
                          <span>❤️ {post.likes || 0} likes</span>
                          <span>💬 {post.comment_count || 0} comments</span>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          {/* Share Button */}
                          <ShareButton
                            url={post.ward ? `/ward/ward-${post.ward.replace('Ward ', '')}` : '/dashboard'}
                            text={`I reported a community issue in ${post.ward || 'Regina'}: "${(post.content || '').slice(0, 100)}${(post.content || '').length > 100 ? '...' : ''}" — via CYQR.CA`}
                            size="xs"
                          />

                          {/* Admin Feature Toggle */}
                          {isAdmin() && (
                            <button
                              onClick={() => handleToggleFeatured(post.id, post.is_featured)}
                              className={`flex items-center gap-2 font-display text-xs py-2 px-4 tracking-wider transition-colors ${
                                post.is_featured
                                  ? 'bg-[#C5A52A] text-black hover:bg-[#D4B93A]'
                                  : 'bg-[#1a1a1a] text-gray-400 hover:bg-[#2a2a2a]'
                              }`}
                              title={post.is_featured ? 'Remove featured status' : 'Feature this post'}
                              data-testid={`feature-toggle-${post.id}`}
                            >
                              <Star className={`w-3 h-3 ${post.is_featured ? 'fill-current' : ''}`} />
                              {post.is_featured ? 'FEATURED' : 'FEATURE'}
                            </button>
                          )}
                          
                          {/* Resolve/Unresolve Button */}
                          {post.resolved_at ? (
                            <button
                              onClick={() => handleUnresolve(post.id)}
                              className="flex items-center gap-2 bg-[#1a1a1a] hover:bg-[#2a2a2a] text-gray-400 font-display text-xs py-2 px-4 tracking-wider transition-colors"
                            >
                              <Undo2 className="w-3 h-3" />
                              MARK UNRESOLVED
                            </button>
                          ) : (
                            <button
                              onClick={() => handleResolve(post.id)}
                              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-display text-xs py-2 px-4 tracking-wider transition-colors"
                              data-testid={`resolve-btn-${post.id}`}
                            >
                              <CheckCircle className="w-3 h-3" />
                              MARK AS RECTIFIED
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : activeTab === 'messages' ? (
          /* Messages Section */
          <>
            <div className="flex items-center justify-between mb-4">
              <p className="font-mono text-xs text-gray-500">
                Anonymous messages sent through the Relay Gateway
              </p>
              <Link
                to="/relay"
                className="flex items-center gap-2 bg-[#1a1a1a] hover:bg-[#2a2a2a] text-white font-display text-xs py-2 px-4 tracking-wider transition-colors"
              >
                <Send className="w-3 h-3" />
                NEW MESSAGE
              </Link>
            </div>

            {/* Filter Buttons */}
            <div className="flex items-center gap-2 mb-6">
              <button
                onClick={() => setMessageFilter('all')}
                className={`font-display text-xs py-2 px-4 tracking-wider transition-colors ${
                  messageFilter === 'all'
                    ? 'bg-[#C5A52A] text-black'
                    : 'bg-[#1a1a1a] text-gray-400 hover:bg-[#2a2a2a]'
                }`}
              >
                ALL ({messages.length})
              </button>
              <button
                onClick={() => setMessageFilter('pending')}
                className={`font-display text-xs py-2 px-4 tracking-wider transition-colors ${
                  messageFilter === 'pending'
                    ? 'bg-yellow-600 text-black'
                    : 'bg-[#1a1a1a] text-gray-400 hover:bg-[#2a2a2a]'
                }`}
              >
                PENDING ({messages.filter(m => m.status === 'pending').length})
              </button>
              <button
                onClick={() => setMessageFilter('replied')}
                className={`font-display text-xs py-2 px-4 tracking-wider transition-colors ${
                  messageFilter === 'replied'
                    ? 'bg-green-600 text-black'
                    : 'bg-[#1a1a1a] text-gray-400 hover:bg-[#2a2a2a]'
                }`}
              >
                REPLIED ({messages.filter(m => m.status === 'replied').length})
              </button>
            </div>

            {messages.length === 0 ? (
              <div className="text-center py-12 bg-[#111] border border-[#1a1a1a] rounded">
                <Mail className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-500 font-mono mb-4">No messages sent yet</p>
                <Link
                  to="/relay"
                  className="inline-flex items-center gap-2 bg-[#C5A52A] hover:bg-[#D4B93A] text-black font-display text-sm py-2 px-6 tracking-wider transition-colors"
                >
                  <Send className="w-4 h-4" />
                  CONTACT A COUNCILLOR
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {messages
                  .filter(m => messageFilter === 'all' || m.status === messageFilter)
                  .sort((a, b) => {
                    // Unread replies first, then by date
                    if (a.status === 'replied' && !a.read && b.status !== 'replied') return -1;
                    if (b.status === 'replied' && !b.read && a.status !== 'replied') return 1;
                    return new Date(b.created_at) - new Date(a.created_at);
                  })
                  .map((message) => (
                  <div
                    key={message.id}
                    className={`bg-[#111] border rounded overflow-hidden transition-all ${
                      message.status === 'replied' && !message.read
                        ? 'border-green-500/50 shadow-[0_0_20px_rgba(34,197,94,0.2)]'
                        : 'border-[#1a1a1a]'
                    }`}
                    data-testid={`message-${message.id}`}
                  >
                    <div className="p-4 border-b border-[#1a1a1a] flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-display text-sm text-white tracking-wider">
                            TO: {message.councillor_name?.toUpperCase()}
                          </h3>
                          {message.status === 'replied' && !message.read && (
                            <span className="bg-green-500 text-black text-[10px] font-display px-2 py-0.5 rounded tracking-wider">
                              NEW REPLY
                            </span>
                          )}
                        </div>
                        <p className="font-mono text-[10px] text-gray-600">
                          Sent: {formatDate(message.created_at)} • REF: {message.anonymous_id}
                        </p>
                      </div>
                      <span
                        className={`flex items-center gap-1 px-2 py-1 rounded ${
                          message.status === 'replied'
                            ? 'bg-green-900/30 border border-green-800/50'
                            : message.status === 'archived'
                            ? 'bg-gray-900/30 border border-gray-800/50'
                            : 'bg-yellow-900/30 border border-yellow-800/50'
                        }`}
                      >
                        {message.status === 'replied' ? (
                          <CheckCircle className="w-3 h-3 text-green-500" />
                        ) : message.status === 'archived' ? (
                          <Clock className="w-3 h-3 text-gray-500" />
                        ) : (
                          <Clock className="w-3 h-3 text-yellow-500" />
                        )}
                        <span className={`font-mono text-[10px] ${
                          message.status === 'replied' ? 'text-green-500' 
                          : message.status === 'archived' ? 'text-gray-500'
                          : 'text-yellow-500'
                        }`}>
                          {message.status === 'replied' ? 'REPLIED' 
                           : message.status === 'archived' ? 'NO RESPONSE'
                           : 'PENDING'}
                        </span>
                      </span>
                    </div>

                    <div className="p-4">
                      <p className="font-mono text-xs text-gray-500 mb-2 uppercase">Your Message:</p>
                      <p className="text-gray-300 leading-relaxed text-sm">{message.message}</p>
                    </div>

                    {message.reply && (
                      <div className="p-4 bg-[#0a0a0a] border-t border-[#1a1a1a]">
                        <div className="flex items-center justify-between mb-2">
                          <p className="font-mono text-xs text-green-500 uppercase">
                            Response from {message.councillor_name}:
                          </p>
                          {message.replied_at && (
                            <p className="font-mono text-[10px] text-gray-600">
                              Replied {formatDate(message.replied_at)}
                              {message.reminder_sent && ' (after reminder)'}
                            </p>
                          )}
                        </div>
                        <p className="text-white leading-relaxed text-sm bg-[#111] p-3 rounded border border-[#1a1a1a] whitespace-pre-wrap">
                          {message.reply}
                        </p>
                      </div>
                    )}
                    
                    {message.status === 'pending' && message.reminder_sent && (
                      <div className="p-3 bg-yellow-900/20 border-t border-yellow-800/30">
                        <p className="font-mono text-xs text-yellow-500">
                          ⚠️ Reminder sent {formatDate(message.reminder_sent_at)} - Awaiting response
                        </p>
                      </div>
                    )}
                    
                    {message.status === 'archived' && (
                      <div className="p-3 bg-gray-900/20 border-t border-gray-800/30">
                        <p className="font-mono text-xs text-gray-500">
                          No response received after 14 days. Message archived.
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        ) : null}
      </div>
    </Layout>
  );
};

export default Dashboard;
